import { TokenMetadata } from '../../../services/ft-contract';
import React, { useState, useContext, Fragment, useRef } from 'react';
import { getTxId } from '../../../services/indexer';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  toReadableNumber,
  calculateFeePercent,
  toPrecision,
} from '../../../utils/numbers';
import { V3_POOL_SPLITTER, pointToPrice } from '../../../services/swapV3';
import { toRealSymbol } from '../../../utils/token';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import { TIMESTAMP_DIVISOR } from 'src/constants/timestampDivisor';
import { HiOutlineExternalLink } from '../../reactIcons/index';
import getConfig from '../../../services/config';
import {
  NearblocksIcon,
  PikespeakIcon,
  TxLeftArrow,
} from 'src/components/icon/Pool';
import { PriceContext, MobileInfoBanner } from './myOrderShared';
import {
  MyOrderInstantSwapArrowRight,
  MobileHistoryOrderStamp,
} from '../../icon/swapV3';
import { MyOrderMobileArrow } from '../../icon/Arrows';
import { TOKEN_LIST_FOR_RATE } from '../../../services/dcl/dclDisplay';

export function HistorySwapInfoLine({
  index,
  tokensMap,
  orderTx,
  token_in,
  token_out,
  pool_id,
  point,
  amount_in,
  amount_out,
  timestamp,
}: {
  index: number;
  tokensMap: { [key: string]: TokenMetadata };
  orderTx: string;
  token_in: string;
  token_out: string;
  pool_id: string;
  point: string;
  amount_in: string;
  amount_out: string;
  timestamp: string;
  hoverOn: number;
  setHoverOn: React.Dispatch<React.SetStateAction<number>>;
}) {
  const [loadingStates, setLoadingStates] = useState({});
  const [hoveredTx, setHoveredTx] = useState(null);
  const closeTimeoutRef = useRef(null);

  const handleMouseEnter = (receipt_id) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHoveredTx(receipt_id);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredTx(null);
    }, 200);
  };

  async function handleTxClick(receipt_id, url) {
    setLoadingStates((prevStates) => ({ ...prevStates, [receipt_id]: true }));
    try {
      const txIdResult = await getTxId(receipt_id);
      if (txIdResult && txIdResult.receipts && txIdResult.receipts.length > 0) {
        const txHash = txIdResult.receipts[0].originated_from_transaction_hash;
        window.open(`${url}/${txHash}`, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error(
        'An error occurred while fetching transaction data:',
        error
      );
    } finally {
      setLoadingStates((prevStates) => ({
        ...prevStates,
        [receipt_id]: false,
      }));
    }
  }
  const intl = useIntl();

  const buyToken = tokensMap[token_out];

  const sellToken = tokensMap[token_in];

  const [hover, setHover] = useState<boolean>(false);

  if (!buyToken || !sellToken) return null;

  const orderIn = toReadableNumber(sellToken.decimals, amount_in || '0');

  const calPoint =
    sellToken.id === pool_id.split(V3_POOL_SPLITTER)[0]
      ? Number(point)
      : -Number(point);

  const price = pointToPrice({
    tokenA: sellToken,
    tokenB: buyToken,
    point: calPoint,
  });

  const buyAmount = toReadableNumber(buyToken.decimals, amount_out || '0');

  const sellTokenAmount = (
    <span className="flex py-4 pl-3  flex-shrink-0 items-center">
      <img
        src={sellToken.icon}
        className="border border-gradientFrom rounded-full w-7 h-7"
        alt=""
      />

      <div className="flex   xs:flex-row flex-col ml-2">
        <span className="text-white text-sm mr-2" title={orderIn}>
          {Number(orderIn) > 0 && Number(orderIn) < 0.01
            ? '< 0.01'
            : toPrecision(orderIn, 2)}
        </span>

        <span className="text-v3SwapGray text-xs xs:relative xs:top-0.5">
          {toRealSymbol(sellToken.symbol)}
        </span>
      </div>
    </span>
  );

  const buyTokenAmount = (
    <span className="flex items-center col-span-1 ">
      <img
        src={buyToken.icon}
        className="border flex-shrink-0 border-gradientFrom rounded-full w-7 h-7"
        alt=""
      />

      <div className="flex xs:flex-row flex-col ml-2">
        <span
          className="text-white mr-2 text-sm whitespace-nowrap"
          title={buyAmount}
        >
          {Number(buyAmount) > 0 && Number(buyAmount) < 0.01
            ? '< 0.01'
            : toPrecision(buyAmount, 2)}
        </span>

        <span className="text-v3SwapGray text-xs xs:relative xs:top-0.5">
          {toRealSymbol(buyToken.symbol)}
        </span>
      </div>
    </span>
  );

  const fee = Number(pool_id.split(V3_POOL_SPLITTER)[2]);

  const feeTier = (
    <span className="rounded-lg relative xsm:right-0 xsm:bg-none right-3 text-left  text-primaryText p-1 lg:bg-menuMoreBgColor xs:text-white">
      {`${toPrecision(calculateFeePercent(fee / 100).toString(), 2)}% `}
    </span>
  );
  const sort =
    TOKEN_LIST_FOR_RATE.indexOf(sellToken?.symbol) > -1 && +price !== 0;
  const calcPrice = sort ? new BigNumber(1).dividedBy(price).toFixed() : price;

  const orderRate = (
    <span className="whitespace-nowrap col-span-1 flex items-start xs:flex-row xs:items-center flex-col relative  xs:right-0">
      <span className="mr-1 text-white text-sm" title={calcPrice}>
        {toPrecision(calcPrice, 2)}
      </span>
      <span className="text-v3SwapGray text-xs xs:hidden">
        {`${toRealSymbol(
          sort ? sellToken.symbol : buyToken.symbol
        )}/${toRealSymbol(sort ? buyToken.symbol : sellToken.symbol)}`}
      </span>
      <span className="text-white text-sm lg:hidden md:hidden">
        {`${toRealSymbol(sort ? sellToken.symbol : buyToken.symbol)}`}
      </span>
    </span>
  );

  const claimed = (
    <span className="whitespace-nowrap col-span-2 xs:flex-col flex items-center ml-12">
      <div>
        <div className="flex items-center xs:justify-end">
          <img
            src={buyToken.icon}
            className="border border-gradientFrom rounded-full w-4 h-4"
            alt=""
          />
          <span
            className="text-white text-sm mx-1"
            title={toReadableNumber(buyToken.decimals, amount_out || '0')}
          >
            {Number(toReadableNumber(buyToken.decimals, amount_out || '0')) >
              0 &&
            Number(toReadableNumber(buyToken.decimals, amount_out || '0')) <
              0.001
              ? '< 0.001'
              : toPrecision(
                  toReadableNumber(buyToken.decimals, amount_out || '0'),
                  3
                )}
          </span>
        </div>
      </div>
    </span>
  );

  const created = (
    <span className=" relative  whitespace-nowrap    text-primaryText xs:text-xs flex flex-col   xsm:justify-center  text-left xs:opacity-50">
      <span className="xsm:hidden">
        {moment(
          Math.floor(Number(timestamp) / TIMESTAMP_DIVISOR) * 1000
        ).format('YYYY-MM-DD')}
      </span>
      <span className="xsm:hidden">
        {moment(
          Math.floor(Number(timestamp) / TIMESTAMP_DIVISOR) * 1000
        ).format('HH:mm')}
      </span>

      <span className="lg:hidden text-center relative bottom-2">
        {moment(
          Math.floor(Number(timestamp) / TIMESTAMP_DIVISOR) * 1000
        ).format('YYYY-MM-DD HH:mm')}
      </span>
    </span>
  );

  const actions = (
    <div className=" col-span-1 pr-4  text-primaryText  text-xs flex flex-col items-end justify-self-end p-1.5">
      <span className="flex items-center text-sm text-white whitespace-nowrap">
        {<FormattedMessage id="executed" defaultMessage={'Executed'} />}
      </span>
      <div className="relative">
        {!!orderTx && (
          <a
            className="flex items-center text-v3SwapGray cursor-pointer"
            onMouseEnter={() => handleMouseEnter(orderTx)}
            onMouseLeave={handleMouseLeave}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {loadingStates[orderTx] ? (
              <>
                Tx
                <span className="loading-dots"></span>
              </>
            ) : (
              <>
                Tx
                <span className="ml-1.5">
                  <HiOutlineExternalLink />
                </span>
              </>
            )}
            {hoveredTx === orderTx && (
              <div className="w-44 absolute top-6 left-0 bg-poolDetaileTxBgColor border border-poolDetaileTxBorderColor rounded-lg p-2 shadow-lg z-50">
                <div className="flex flex-col">
                  <div
                    className="mb-2 px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                    onMouseEnter={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'block';
                      }
                    }}
                    onMouseLeave={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'none';
                      }
                    }}
                    onClick={() =>
                      handleTxClick(orderTx, `${getConfig().explorerUrl}/txns`)
                    }
                  >
                    <NearblocksIcon />
                    <p className="ml-2 text-sm">nearblocks</p>
                    <div className="ml-3 arrow" style={{ display: 'none' }}>
                      <TxLeftArrow />
                    </div>
                  </div>
                  <div
                    className="px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                    onMouseEnter={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'block';
                      }
                    }}
                    onMouseLeave={(event) => {
                      const arrow = event.currentTarget.querySelector(
                        '.arrow'
                      ) as HTMLElement;
                      if (arrow) {
                        arrow.style.display = 'none';
                      }
                    }}
                    onClick={() =>
                      handleTxClick(
                        orderTx,
                        `${getConfig().pikespeakUrl}/transaction-viewer`
                      )
                    }
                  >
                    <PikespeakIcon />
                    <p className="ml-2 text-sm">Pikespeak...</p>
                    <div className="ml-3 arrow" style={{ display: 'none' }}>
                      <TxLeftArrow />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </a>
        )}
      </div>
    </div>
  );

  const tokenPrice = useContext(PriceContext);

  return (
    <>
      <Fragment>
        <tr>
          <td>
            <div className="pb-2.5"></div>
          </td>
        </tr>
        <tr
          className={`mb-4 overflow-visible   xs:hidden px-4 py-3 text-sm   z-20   relative  w-full rounded-xl items-center   hover:bg-portfolioBarBgColor bg-portfolioBgColor `}
          style={{
            zIndex: 21,
          }}
        >
          <td className="rounded-l-xl">{sellTokenAmount}</td>

          <td>
            <span className="text-white text-lg frcs w-7 xs:hidden ">
              <MyOrderInstantSwapArrowRight />
            </span>
          </td>

          <td>{buyTokenAmount}</td>

          <td>{feeTier}</td>

          <td>{orderRate}</td>

          <td>{created}</td>

          <td className=""></td>
          <td className="rounded-r-xl">{actions}</td>
        </tr>

        {/* {hover && !ONLY_ZEROS.test(swapIn || '0') ? swapBanner : null} */}
      </Fragment>

      <div
        className="w-full relative mb-4 md:hidden lg:hidden"
        style={{
          zIndex: 20 - index,
        }}
      >
        <MobileHistoryOrderStamp state={'swapped'}></MobileHistoryOrderStamp>

        {/* title */}
        <div className="rounded-t-xl relative bg-orderMobileTop px-3 pt-3">
          <div className="flex items-center relative justify-between">
            {sellTokenAmount}
            <MyOrderMobileArrow />
            {buyTokenAmount}
          </div>

          {created}

          <div className="absolute right-4 bottom-2.5 z-50  text-xs">
            <div className="relative">
              {!!orderTx && (
                <a
                  className="flex items-center text-v3SwapGray cursor-pointer"
                  onMouseEnter={() => handleMouseEnter(orderTx)}
                  onMouseLeave={handleMouseLeave}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  {loadingStates[orderTx] ? (
                    <>
                      Tx
                      <span className="loading-dots"></span>
                    </>
                  ) : (
                    <>
                      Tx
                      <span className="ml-1.5">
                        <HiOutlineExternalLink />
                      </span>
                    </>
                  )}
                  {hoveredTx === orderTx && (
                    <div className="w-44 absolute top-6 right-0 bg-poolDetaileTxBgColor border border-poolDetaileTxBorderColor rounded-lg p-2 shadow-lg z-50">
                      <div className="flex flex-col">
                        <div
                          className="mb-2 px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                          onMouseEnter={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'block';
                            }
                          }}
                          onMouseLeave={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'none';
                            }
                          }}
                          onClick={() =>
                            handleTxClick(
                              orderTx,
                              `${getConfig().explorerUrl}/txns`
                            )
                          }
                        >
                          <NearblocksIcon />
                          <p className="ml-2 text-sm">nearblocks</p>
                          <div
                            className="ml-3 arrow"
                            style={{ display: 'none' }}
                          >
                            <TxLeftArrow />
                          </div>
                        </div>
                        <div
                          className="px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                          onMouseEnter={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'block';
                            }
                          }}
                          onMouseLeave={(event) => {
                            const arrow = event.currentTarget.querySelector(
                              '.arrow'
                            ) as HTMLElement;
                            if (arrow) {
                              arrow.style.display = 'none';
                            }
                          }}
                          onClick={() =>
                            handleTxClick(
                              orderTx,
                              `${getConfig().pikespeakUrl}/transaction-viewer`
                            )
                          }
                        >
                          <PikespeakIcon />
                          <p className="ml-2 text-sm">Pikespeak...</p>
                          <div
                            className="ml-3 arrow"
                            style={{ display: 'none' }}
                          >
                            <TxLeftArrow />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </a>
              )}
            </div>
          </div>
        </div>
        {/*  content */}
        <div className="rounded-b-xl p-3 pb-1 bg-cardBg">
          <MobileInfoBanner
            text={
              <FormattedMessage id="fee_tiers" defaultMessage={'Fee Tiers'} />
            }
            value={feeTier}
          />

          <MobileInfoBanner
            text={`1 ${toRealSymbol(
              sort ? buyToken?.symbol : tokensMap[token_in].symbol
            )} Price`}
            value={orderRate}
          />

          <MobileInfoBanner
            text={
              <FormattedMessage defaultMessage={'Executed'} id="executed" />
            }
            value={claimed}
          />
        </div>
      </div>
    </>
  );
}
